import React from "react";
import {
    Dialog,
    DialogTrigger,
    Form,
    GridListItem,
    Modal,
    ModalOverlay,
    VisuallyHidden,
} from "react-aria-components";
import { msgid, ngettext, t } from "ttag";

import type {
    ArticlePageTile,
    ServerCmsModelsPagesArticleArticleTagLink,
} from "@reactivated";
import { Context } from "@reactivated";

import { ButtonTertiary, ButtonTextPrimary, Clickable } from "../Clickables";
import { ALL_VALUE, Filters, List } from "../FilterableList";
import { SearchField } from "../Search";
import { useAlgoliaSearch } from "../Search/useAlgoliaSearch";
import { Separator } from "../Separator";
import { Svg } from "../Svg";

import filterableListStyles from "../FilterableList/FilterableList.module.scss";
import styles from "./search-results.module.scss";

type ArticleTagLink = ServerCmsModelsPagesArticleArticleTagLink;

const groups = {
    TYPE: "type",
    TOPIC: "topic",
    AUDIENCE: "audience",
} as const;

const unfiltered = {
    [groups.TYPE]: ALL_VALUE,
    [groups.TOPIC]: ALL_VALUE,
    [groups.AUDIENCE]: ALL_VALUE,
} as const;

export const SearchResults = ({ filters }: { filters: ArticleTagLink[] }) => {
    const context = React.useContext(Context);

    const initialQuery =
        new URL(context.request.url).searchParams.get("q") ?? "";

    const {
        query,
        setQuery,
        filterValues,
        onFilterChange,
        resetFilters,
        items,
        loadMore,
        totalResults,
        hasMorePages,
        substituteQuery,
        activeQuery,
        hasSearched,
    } = useAlgoliaSearch({
        algoliaConfig: context.algolia,
        initialQuery,
        initialFilterValues: unfiltered,
    });

    const [inputValue, setInputValue] = React.useState(initialQuery);

    const handlePopState = React.useEffectEvent(() => {
        const q = new URL(window.location.href).searchParams.get("q") ?? "";
        setInputValue(q);
        setQuery(q);
    });

    React.useEffect(() => {
        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, []);

    const mappedItems = items.map(
        (item) =>
            ({
                id: Number(item.objectID) || 0,
                url: item.url,
                title: item.title,
                tile_type: "article" as const,
                tags: item.tags ?? [],
                search_description: item.search_description,
                category: item.article_type ?? item.subtitle ?? null,
                image: item.image || item.seo_image,
                image_aspect_ratio: "landscape",
            }) satisfies ArticlePageTile,
    );

    return (
        <>
            <Form
                onSubmit={(e) => {
                    e.preventDefault();
                    const url = new URL(window.location.href);
                    url.searchParams.set("q", inputValue);
                    history.pushState({}, "", url.toString());
                    setQuery(inputValue);
                }}
                className={styles.form}
            >
                <SearchField value={inputValue} onChange={setInputValue}>
                    <Clickable slot={null} type="submit">
                        <VisuallyHidden>{t`Search`}</VisuallyHidden>
                        <Svg aria-hidden name="search" />
                    </Clickable>
                </SearchField>
            </Form>

            {hasSearched && (
                <>
                    <p className={styles.counter}>
                        {substituteQuery && t`No results for ${query}.  `}
                        {t`Showing ${totalResults} results for`}&nbsp;
                        <q>{activeQuery}</q>
                    </p>
                    <div className={styles.spacer} />
                    <div className={filterableListStyles.filters}>
                        <DialogTrigger>
                            <div className={filterableListStyles.filterTop}>
                                <Clickable
                                    className={
                                        filterableListStyles.mobileTrigger
                                    }
                                >
                                    {t`Filter`}
                                    <Svg name="filter" aria-hidden />
                                </Clickable>
                                <span
                                    className={filterableListStyles.resultCount}
                                >
                                    {ngettext(
                                        msgid`${totalResults} Result`,
                                        `${totalResults} Results`,
                                        totalResults,
                                    )}
                                </span>
                            </div>
                            <ModalOverlay
                                className={filterableListStyles.modalOverlay}
                                isDismissable
                            >
                                <Modal className={filterableListStyles.modal}>
                                    <Dialog
                                        className={filterableListStyles.dialog}
                                        aria-label={t`Filters`}
                                    >
                                        {({ close }) => (
                                            <>
                                                <Clickable
                                                    className={
                                                        filterableListStyles.closeButton
                                                    }
                                                    onClick={close}
                                                >
                                                    <span aria-hidden>
                                                        {t`Filter`}{" "}
                                                        <Svg name="filter" />
                                                    </span>
                                                    <span>
                                                        <VisuallyHidden>
                                                            {t`Close`}
                                                        </VisuallyHidden>
                                                        <Svg
                                                            name="close"
                                                            aria-hidden
                                                        />
                                                    </span>
                                                </Clickable>
                                                <Separator orientation="horizontal" />
                                                <Filters
                                                    categories={filters}
                                                    values={filterValues}
                                                    onValuesChange={
                                                        onFilterChange
                                                    }
                                                    className={
                                                        filterableListStyles.modalFilters
                                                    }
                                                />
                                                <ButtonTertiary onClick={close}>
                                                    {t`Show Results`}
                                                </ButtonTertiary>
                                                <ButtonTextPrimary
                                                    onClick={() => {
                                                        resetFilters();
                                                        close();
                                                    }}
                                                >
                                                    {t`Clear All`}
                                                </ButtonTextPrimary>
                                            </>
                                        )}
                                    </Dialog>
                                </Modal>
                            </ModalOverlay>
                        </DialogTrigger>
                        <Filters
                            categories={filters}
                            values={filterValues}
                            onValuesChange={onFilterChange}
                            className={filterableListStyles.desktop}
                        />
                    </div>
                    <List items={mappedItems}>
                        {hasMorePages && (
                            <GridListItem
                                onAction={loadMore}
                                className={styles.loadMore}
                            >
                                {t`Load More`}
                            </GridListItem>
                        )}
                    </List>
                </>
            )}
        </>
    );
};
